<template>
  <div v-if="open" class="photo-gallery-overlay" @click.self="close">
    <div class="photo-gallery">
      <button type="button" class="photo-gallery-close" aria-label="Закрыть" @click="close">
        &times;
      </button>

      <div v-if="displayPhotos.length" class="photo-gallery-body">
        <button
          v-if="displayPhotos.length > 1"
          type="button"
          class="photo-gallery-nav photo-gallery-nav-prev"
          aria-label="Предыдущее фото"
          @click="prev"
        >
          &#8249;
        </button>

        <img
          :src="displayPhotos[currentIndex].url"
          :alt="displayPhotos[currentIndex].filename || 'Фото'"
          class="photo-gallery-image"
        />

        <button
          v-if="displayPhotos.length > 1"
          type="button"
          class="photo-gallery-nav photo-gallery-nav-next"
          aria-label="Следующее фото"
          @click="next"
        >
          &#8250;
        </button>
      </div>

      <div class="photo-gallery-footer">
        <p v-if="displayPhotos.length > 1" class="photo-gallery-counter">
          {{ currentIndex + 1 }} / {{ displayPhotos.length }}
        </p>
        <p v-if="comment?.body" class="photo-gallery-caption">{{ comment.body }}</p>
        <AuthorLink
          v-if="comment?.author"
          :author="comment.author"
          size="sm"
          class="photo-gallery-meta"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import AuthorLink from './AuthorLink.vue'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  comment: {
    type: Object,
    default: null,
  },
  photos: {
    type: Array,
    default: null,
  },
  initialPhotoIndex: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['close'])

const currentIndex = ref(0)
const displayPhotos = computed(() => {
  if (props.photos?.length) return props.photos
  return props.comment?.photos || []
})

watch(
  () => [props.open, props.comment, props.initialPhotoIndex],
  () => {
    if (!props.open) return
    currentIndex.value = props.initialPhotoIndex
  }
)

function close() {
  emit('close')
}

function prev() {
  if (!displayPhotos.value.length) return
  currentIndex.value = (currentIndex.value - 1 + displayPhotos.value.length) % displayPhotos.value.length
}

function next() {
  if (!displayPhotos.value.length) return
  currentIndex.value = (currentIndex.value + 1) % displayPhotos.value.length
}

function onKeydown(event) {
  if (!props.open) return

  if (event.key === 'Escape') {
    close()
  } else if (event.key === 'ArrowLeft') {
    prev()
  } else if (event.key === 'ArrowRight') {
    next()
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      window.addEventListener('keydown', onKeydown)
    } else {
      window.removeEventListener('keydown', onKeydown)
    }
  }
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.photo-gallery-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.photo-gallery {
  position: relative;
  background: #fff;
  border-radius: 0.75rem;
  max-width: min(90vw, 900px);
  width: 100%;
  overflow: hidden;
}

.photo-gallery-close {
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  z-index: 1;
  border: none;
  background: transparent;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}

.photo-gallery-body {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  min-height: 320px;
}

.photo-gallery-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.photo-gallery-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  font-size: 1.5rem;
  cursor: pointer;
}

.photo-gallery-nav-prev {
  left: 0.75rem;
}

.photo-gallery-nav-next {
  right: 0.75rem;
}

.photo-gallery-footer {
  padding: 1rem 1.25rem;
}

.photo-gallery-counter {
  margin: 0 0 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
}

.photo-gallery-caption {
  margin: 0 0 0.25rem;
  white-space: pre-wrap;
}

.photo-gallery-meta {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}
</style>
